/**
 * Maturity Assessment Results Screen
 * 
 * Comprehensive results display featuring:
 * - Quadrant-based maturity positioning (Manual, Siloed, Managed, Autonomous)
 * - Operational and Strategic maturity scoring with visual charts
 * - Detailed response breakdown with selected options
 * - PDF export functionality with professional formatting
 * - Strategic recommendations and next steps
 * - Implementation blockers analysis
 * 
 * Used after assessment completion to show insights and actionable guidance
 */

import { useLocation, useParams } from "wouter";
import { MATURITY_BANDS, BAND_THRESHOLDS } from './maturityOnlyResults/constants';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from "@/components/ui/button";
import { useEffect, useMemo, useRef, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Target, AlertTriangle, TrendingUp, FileText, BarChart3, Info, Lightbulb, Users } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";

import { MaturityLayout } from "@/layouts/MaturityLayout";
import { useToast } from '@/hooks/use-toast';
import { generateMaturityResults } from "@/utils/maturityBanding";
import { HeaderCard } from '@/components/maturityAssesment/HeaderCard';
import { SectionHeader } from '@/components/maturityAssesment/SectionHeader';
import MaturitySegmentDisplay, { getMaturityRanking } from '@/components/maturityAssesment/MaturitySegmentDisplay';
import DetailedResults from '@/components/maturityAssesment/DetailedResults';
import { useChartImage } from './maturityOnlyResults/useChartImage';
import { useApiAudit } from './maturityOnlyResults/hooks/useApiAudit';
import {MaturityResults} from "@shared/interfaces/maturityResultsInterface.ts";
import { MaturitySegment } from "@shared/interfaces/maturitySegment.interface.ts";
import { RecommendedAction } from "@shared/interfaces/recommendedAction.interface.ts";
import { MaturityAssessmentRecord } from "@shared/interfaces/maturityAssessment.interface.ts";
import { ProjectPayload } from "@shared/interfaces/projectPayload.interface.ts";
import html2canvas from 'html2canvas';
import { jsPDF } from 'jspdf';
import MaturityReportDocument from './components/MaturityReportDocument';


function MaturityOnlyResults() {
  const [, setLocation] = useLocation();
  const { id: projectId } = useParams<{ id: string }>();
  const { toast } = useToast();
  const { user } = useAuth();
  const [maturityResults, setMaturityResults] = useState<MaturityResults | null>(null);
  const [showDetailedResponses, setShowDetailedResponses] = useState(false);
  const [isGeneratingPdf, setIsGeneratingPdf] = useState(false);
  const primaryPageRef = useRef<HTMLDivElement>(null);
  const reportDocRef = useRef<HTMLDivElement>(null);

  // Debug project ID handling
  useEffect(() => {
    console.log("Project ID in MaturityOnlyResults Screen:", projectId);
  }, [projectId]);

  // Fetch project data for breadcrumb (use default queryFn with Authorization)
  const { data: projectData } = useQuery<ProjectPayload | null>({
    queryKey: projectId ? [`/api/projects/${projectId}`] : [],
    enabled: !!projectId,
    retry: false
  });

  // Fetch maturity results for this project (use default queryFn with Authorization)
  const { data: assessmentData, isLoading: maturityLoading } = useQuery<MaturityAssessmentRecord[] | null>({
    queryKey: projectId ? [`/api/maturity/${projectId}`] : [],
    enabled: !!projectId,
    retry: false
  });

  // Fetch dynamic maturity segment data (use default queryFn with Authorization)
  const { data: maturitySegmentData } = useQuery<MaturitySegment | null>({
    queryKey: (maturityResults?.operationalBand && maturityResults?.strategicBand)
      ? [`/api/maturity-segment?operational=${encodeURIComponent(maturityResults.operationalBand)}&strategic=${encodeURIComponent(maturityResults.strategicBand)}`]
      : [],
    enabled: !!(maturityResults?.operationalBand && maturityResults?.strategicBand),
  });
  
  // Fetch recommended actions (use default queryFn with Authorization)
  const { data: recommendedActionsData } = useQuery<RecommendedAction[] | null>({
    queryKey: maturitySegmentData?.id ? [`/api/recommended-actions?segmentId=${encodeURIComponent(maturitySegmentData.id)}`] : [],
    enabled: !!maturitySegmentData?.id,
  });

  // Capture chart image once data is available
  const { chartImage, chartRef } = useChartImage(assessmentData, maturityResults);


  // Process assessment data into a new maturity results format
  useEffect(() => {
    // Get the most recent assessment data (first item in array)
    const latestAssessment = assessmentData && Array.isArray(assessmentData) && assessmentData.length > 0 ? assessmentData[0] : null;
    
    // Handle both new array format and legacy object format
    const extractAnswers = (assessment: any): number[] => {
      if (!assessment?.data) return [];
      
      // New format: data is array  
      if (Array.isArray(assessment.data)) {
        return assessment.data;
      }
      
      // Legacy format: data is object with answers property
      if (assessment.data.answers && Array.isArray(assessment.data.answers)) {
        return assessment.data.answers;
      }
      
      return [];
    };
    
    const answers = extractAnswers(latestAssessment);
    
    if (latestAssessment && answers.length > 0) {
      
      // Calculate operational score (questions 1-6)
      const operationalAnswers = answers.slice(0, 6);
      const operationalScore = operationalAnswers.reduce((sum: number, score: number) => sum + score, 0);

      // Calculate strategic score (questions 7-11)
      const strategicAnswers = answers.slice(6, 11);
      const strategicScore = strategicAnswers.reduce((sum: number, score: number) => sum + score, 0);
      
      // Generate maturity results using new banding system
      const results = generateMaturityResults(operationalScore, strategicScore);
      setMaturityResults(results);
      
      // ========================================
      // 3) ENHANCED RUNTIME MAPPING with DATABASE FIELDS
      // ========================================
      
      // Pre-change audit logging
      console.log("✅ Pre-change theme coverage check", {
        totalQuestions: answers.length,
        assessmentAnswers: answers,
        operationalScore,
        strategicScore
      });
      
      // Define a type for triggered insight items to help with TS narrowing
      interface TriggeredInsight {
        questionId: string;
        questionIndex: number;
        score: number;
        threshold: number;
        isTriggered: boolean;
        impact_weight: number;
        theme_id: any;
        strategic_theme: string;
        dimension: 'Operational' | 'Strategic';
      }

      // Create a mapping function that will use database trigger thresholds
      const computeTriggeredThemes = async (answers: number[]): Promise<TriggeredInsight[]> => {
        try {
          // Fetch question insights with new fields (attach Authorization)
          const token = localStorage.getItem('jwt_token');
          const insightsResponse = await fetch('/api/question-insights', {
            credentials: 'include',
            headers: {
              ...(token ? { Authorization: `Bearer ${token}` } : {}),
              'Accept': 'application/json'
            }
          });
          if (!insightsResponse.ok) {
            console.warn('Failed to fetch question insights, using fallback');
            return computeLegacyThemes(answers);
          }
          
          const insights = await insightsResponse.json();
          console.log('🧪 Question insights with new fields:', insights);
          
          const triggeredInsights = answers.map((score: number, index: number) => {
            const questionId = `Q${index + 1}`;
            const insight = insights.find((i: any) => i.id === questionId);
            
            if (!insight) {
              console.warn(`No insight found for ${questionId}`);
              return null;
            }
            
            const threshold = insight.trigger_score_threshold ?? 3;
            const isTriggered = score < threshold;
            
            return {
              questionId,
              questionIndex: index,
              score,
              threshold,
              isTriggered,
              impact_weight: insight.impact_weight ?? 1,
              theme_id: insight.theme_id,
              strategic_theme: insight.strategic_theme,
              dimension: index < 5 ? 'Operational' : 'Strategic'
            };
          }).filter((item): item is TriggeredInsight => !!item && (item as any).isTriggered);
          
          console.log("🧪 Low-score triggers (post-change)", { 
            triggeredThemes: triggeredInsights.map(t => t?.strategic_theme),
            thresholdsUsed: triggeredInsights.map(t => ({ q: t?.questionId, threshold: t?.threshold }))
          });
          
          return triggeredInsights;
        } catch (error) {
          console.error('Error fetching insights, using fallback:', error);
          return computeLegacyThemes(answers);
        }
      };
      
      // Fallback function for legacy behavior
      const computeLegacyThemes = (answers: number[]): TriggeredInsight[] => {
        const lowScoreThreshold = 3;
        return answers
          .map((score: number, index: number) => ({
            questionId: `Q${index + 1}`,
            questionIndex: index,
            score: score,
            threshold: lowScoreThreshold,
            isTriggered: score < lowScoreThreshold,
            impact_weight: 3,
            theme_id: null,
            strategic_theme: ['Standardization', 'Standardization', 'Integration', 'Automation Gap', 'Visibility Gaps', 'Strategic Alignment', 'Governance Weakness', 'Strategic Enablement', 'Orchestration Gap', 'Strategic Planning', 'Strategic Enablement'][index],
            dimension: index < 5 ? 'Operational' : 'Strategic'
          }))
          .filter((item): item is TriggeredInsight => item.isTriggered);
      };
      
      // Use the enhanced theme computation
      computeTriggeredThemes(answers).then(triggeredInsights => {
        // Sort by impact weight and priority, handling nulls (null-safe secondary sort)
        const sortedInsights = triggeredInsights.sort((a, b) => {
          if (!a && !b) return 0;
          if (!a) return 1;
          if (!b) return -1;

          const aImpact = a?.impact_weight ?? 0;
          const bImpact = b?.impact_weight ?? 0;

          // Primary sort: impact weight (higher first)
          if (bImpact !== aImpact) {
            return bImpact - aImpact;
          }

          // Secondary sort: strategic dimension first (treat missing as 'Operational')
          const dimA = a?.dimension ?? 'Operational';
          const dimB = b?.dimension ?? 'Operational';
          if (dimA !== dimB) {
            return dimA === 'Strategic' ? -1 : 1;
          }
          return 0;
        });
        
        const prioritizedThemes = [...new Set(sortedInsights.map(insight => insight.strategic_theme))];

        
        const selectedSegment = {
          operationalBand: results.operationalBand,
          strategicBand: results.strategicBand,
          segmentId: `${results.operationalBand}_${results.strategicBand}`
        };
        
        const finalGuidancePayload = {
          segment: selectedSegment,
          prioritizedThemes,
          triggeredInsights: sortedInsights,
          recsCount: prioritizedThemes.length,
          lowScoringQuestions: triggeredInsights.length,
          scoringBreakdown: {
            operationalScore,
            strategicScore,
            totalQuestions: answers.length,
            lowScoreCount: triggeredInsights.length
          }
        };
        console.log("🔗 Enhanced guidance payload", finalGuidancePayload);
      });
      
      // Log the new structure to the console for debugging
      console.log('🔍 MaturityResults blob:', JSON.stringify(results, null, 2));
    } else {
      console.log('🔍 No valid assessment data found:', { latestAssessment, hasData: latestAssessment?.data });
    }
  }, [assessmentData]);



  // 2.3 Maturity segments coverage check
  useEffect(() => {
    if (maturityResults?.operationalBand && maturityResults?.strategicBand) {
      const bands = MATURITY_BANDS as unknown as string[];
      const expectedCombos = bands.length * bands.length; // 25 combinations
      const currentCombo = `${maturityResults.operationalBand}_${maturityResults.strategicBand}`;
      const foundCombos = [currentCombo]; // We only see this one combo
      const missingCombos = expectedCombos - foundCombos.length;
      
      // Band thresholds moved to constants
      const thresholdsUsedInCode = BAND_THRESHOLDS;
      
      console.log("🧪 Segment coverage", { 
        expectedCombos, 
        foundCombos, 
        missingCombos, 
        thresholdsUsedInCode,
        currentSegment: currentCombo,
        operationalScore: maturityResults.operationalScore,
        strategicScore: maturityResults.strategicScore
      });
    }
  }, [maturityResults]);

  // ========================================
  // 5) API ENDPOINT BEHAVIOR AUDIT
  // ========================================
  // Add monitoring for all API endpoints used in the results screen via custom hook
  useApiAudit({ projectId, maturityResults, maturitySegmentData });

  // Chart capture moved to useChartImage hook

  // Routing validation
  useEffect(() => {
    if (!projectId) {
      setLocation('/');
      return;
    }
    const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
    if (!uuidRegex.test(projectId)) {
      setLocation('/');
      return;
    }
  }, [projectId, setLocation]);

  // Extract the client name from project data
  const clientName = (projectData as any)?.client_org ||
                   (projectData as any)?.name ||
                   (projectData as any)?.client_name ||
                   '[Client Name Not Set]';

  const exportDate = useMemo(() => new Date().toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric'
  }), []);

  // Shape projectData for MaturityLayout (requires id: string, name: string)
  const layoutProjectData: { id: string; name: string; client_org?: string } | undefined =
    projectData && (projectData as any).id
      ? {
          id: String((projectData as any).id),
          name: (projectData as any).name ?? '[Untitled Project]',
          client_org: (projectData as any).client_org,
        }
      : undefined;

  // Show loading state while assessment query is loading
  if (maturityLoading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-muted-foreground">Loading maturity assessment results...</p>
        </div>
      </div>
    );
  }

  const hasAssessment = Array.isArray(assessmentData) && assessmentData.length > 0;

  // If no assessment records, show empty state
  if (!hasAssessment) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <AlertTriangle className="mx-auto h-12 w-12 text-muted-foreground mb-4" />
          <h2 className="text-xl font-semibold mb-2">No Assessment Data Found</h2>
          <p className="text-muted-foreground mb-4">Complete the maturity assessment to view results.</p>
          <Button onClick={() => setLocation(`/maturity-assessment/${projectId}`)}>
            Take Assessment
          </Button>
        </div>
      </div>
    );
  }

  // Assessments exist but results not yet computed by effect — show a brief loading state
  if (!maturityResults) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-muted-foreground">Preparing results…</p>
        </div>
      </div>
    );
  }

  // Get the actual answers from the latest assessment
  const latestAssessment = Array.isArray(assessmentData) && assessmentData.length > 0 ? assessmentData[0] : null;
  
  // Handle both legacy and new data formats
  const getAnswersFromAssessment = (assessment: any): number[] => {
    if (!assessment?.data) return [];
    
    // New format: data is array
    if (Array.isArray(assessment.data)) {
      return assessment.data;
    }
    
    // Legacy format: data is object with answers property
    if (assessment.data.answers && Array.isArray(assessment.data.answers)) {
      return assessment.data.answers;
    }
    
    return [];
  };
  
  const answers = getAnswersFromAssessment(latestAssessment);
  const actualAnswers = answers;

  const operationalAnswers = actualAnswers.slice(0, 6);
  const strategicAnswers = actualAnswers.slice(6, 11);

  // Correct splits: Operational = Q1–Q6 (0..5), Strategic = Q7–Q11 (6..10)
  const actualOperationalScore = answers.length >= 6
    ? (answers[0] || 0) + (answers[1] || 0) + (answers[2] || 0) + (answers[3] || 0) + (answers[4] || 0) + (answers[5] || 0)
    : maturityResults?.operationalScore || 0;

  const actualStrategicScore = answers.length >= 11
    ? (answers[6] || 0) + (answers[7] || 0) + (answers[8] || 0) + (answers[9] || 0) + (answers[10] || 0)
    : maturityResults?.strategicScore || 0;

  // Calculate the current tier based on actual scores
  const currentTier = getMaturityRanking(actualOperationalScore, actualStrategicScore);



  // Get tier description for current position
  const getTierDescription = (tier: string): string => {
    switch (tier) {
      case 'Autonomous': return 'Fully automated with predictive capabilities';
      case 'Controlled': return 'Orchestrated workflow with real-time visibility';
      case 'Managed': return 'Coordinated processes with selective automation';
      case 'Siloed': return 'Departmental tools with minimal integration';
      case 'Manual': return 'Basic processes with limited automation';
      default: return 'Maturity level assessment';
    }
  };

  const handleGeneratePdf = async () => {
    if (!primaryPageRef.current || !reportDocRef.current) {
      toast({
        title: "Export unavailable",
        description: "PDF content is still loading. Please try again in a moment.",
        variant: "destructive",
      });
      return;
    }

    setIsGeneratingPdf(true);
    const previousScroll = window.scrollY;
    const wasDetailedExpanded = showDetailedResponses;
    const waitForNextFrame = () =>
      new Promise<void>((resolve) => requestAnimationFrame(() => requestAnimationFrame(() => resolve())));

    if (!wasDetailedExpanded) {
      setShowDetailedResponses(true);
      await waitForNextFrame();
    }

    window.scrollTo({ top: 0, behavior: 'auto' });

    const elementsToHide = Array.from(document.querySelectorAll<HTMLElement>('[data-pdf-hidden="true"]'));
    const previousVisibility = elementsToHide.map((element) => element.style.visibility);
    elementsToHide.forEach((element) => {
      element.style.visibility = 'hidden';
    });

    try {
      const pdf = new jsPDF({ orientation: 'portrait', unit: 'pt', format: 'a4', compress: true });
      const pdfWidth = pdf.internal.pageSize.getWidth();
      const pdfHeight = pdf.internal.pageSize.getHeight();

      const resolveBackground = (element: HTMLElement | null) => {
        if (!element) return null;
        const value = getComputedStyle(element).backgroundColor;
        if (!value || value === 'rgba(0, 0, 0, 0)' || value === 'transparent') {
          return null;
        }
        return value;
      };

      const defaultBackground =
        resolveBackground(document.body) ?? resolveBackground(document.documentElement as HTMLElement) ?? '#031c2b';

      const toRGB = (value: string | null | undefined): [number, number, number] | null => {
        if (!value) return null;
        if (value.startsWith('#')) {
          const hex = value.replace('#', '');
          const bigint = parseInt(hex.length === 3 ? hex.split('').map((c) => c + c).join('') : hex, 16);
          return [
            (bigint >> 16) & 255,
            (bigint >> 8) & 255,
            bigint & 255,
          ];
        }

        const rgbMatch = value.match(/rgba?\(([^)]+)\)/i);
        if (rgbMatch) {
          const parts = rgbMatch[1].split(',').map((part) => parseFloat(part.trim()));
          return [parts[0] || 0, parts[1] || 0, parts[2] || 0];
        }
        return null;
      };

      const renderCanvas = async (element: HTMLElement, backgroundColor?: string | null) =>
        html2canvas(element, {
          scale: 2,
          backgroundColor: backgroundColor ?? defaultBackground,
          useCORS: true,
          windowWidth: element.scrollWidth,
          scrollY: -window.scrollY,
        });

      const addCanvasToPdf = (canvas: HTMLCanvasElement, isFirstPage: boolean, backgroundColor?: string | null) => {
        const imageData = canvas.toDataURL('image/png');
        const horizontalMargin = 18;
        const verticalMargin = 32;
        const availableWidth = pdfWidth - horizontalMargin * 2;
        const availableHeight = pdfHeight - verticalMargin * 2;
        const ratio = Math.min(availableWidth / canvas.width, availableHeight / canvas.height);
        const renderWidth = canvas.width * ratio;
        const renderHeight = canvas.height * ratio;
        const offsetX = horizontalMargin + Math.max((availableWidth - renderWidth) / 2, 0);
        const offsetY = verticalMargin + Math.max((availableHeight - renderHeight) / 2, 0);

        if (!isFirstPage) {
          pdf.addPage();
        }

        const fillRGB = toRGB(backgroundColor ?? defaultBackground) ?? [3, 28, 43];
        pdf.setFillColor(fillRGB[0], fillRGB[1], fillRGB[2]);
        pdf.rect(0, 0, pdfWidth, pdfHeight, 'F');

        pdf.addImage(imageData, 'PNG', offsetX, offsetY, renderWidth, renderHeight);
      };

      let isFirstPage = true;

      const reportPages = Array.from(reportDocRef.current.querySelectorAll<HTMLElement>('[data-report-page]'));
      for (const page of reportPages) {
        const pageCanvas = await renderCanvas(page, defaultBackground);
        addCanvasToPdf(pageCanvas, isFirstPage, defaultBackground);
        isFirstPage = false;
      }

      const primarySections = Array.from(
        primaryPageRef.current.querySelectorAll<HTMLElement>('[data-pdf-section]')
      );

      if (primarySections.length) {
        for (const section of primarySections) {
          const sectionBackground = resolveBackground(section) ?? defaultBackground;
          const sectionCanvas = await renderCanvas(section, sectionBackground);
          addCanvasToPdf(sectionCanvas, isFirstPage, sectionBackground);
          isFirstPage = false;
        }
      } else {
        const mainBackground = resolveBackground(primaryPageRef.current) ?? defaultBackground;
        const mainCanvas = await renderCanvas(
          primaryPageRef.current,
          mainBackground,
        );
        addCanvasToPdf(mainCanvas, isFirstPage, mainBackground);
        isFirstPage = false;
      }

      const safeClientName = clientName.replace(/[^a-zA-Z0-9]+/g, '-').replace(/-+/g, '-').replace(/^-|-$/g, '');
      const today = new Date().toISOString().split('T')[0];
      const fileName = `maturity-assessment-${safeClientName || 'report'}-${today}.pdf`;

      pdf.save(fileName);

      toast({
        title: "PDF generated",
        description: "Assessment report downloaded successfully.",
      });
    } catch (error) {
      console.error('PDF generation error:', error);
      toast({
        title: "Export failed",
        description: error instanceof Error ? error.message : 'Unable to generate PDF',
        variant: "destructive",
      });
    } finally {
      elementsToHide.forEach((element, index) => {
        element.style.visibility = previousVisibility[index] ?? '';
      });
      window.scrollTo({ top: previousScroll, behavior: 'auto' });
      if (!wasDetailedExpanded) {
        setShowDetailedResponses(false);
      }
      setIsGeneratingPdf(false);
    }
  };

  return (
    <MaturityLayout section="Maturity Assessment Results" projectData={layoutProjectData}>
      <TooltipProvider>
        <div
          ref={primaryPageRef}
          data-pdf-page="primary"
          className="container mx-auto px-4 sm:px-6 lg:px-8 py-4 sm:py-6 lg:py-8 max-w-6xl"
        >
        <div data-pdf-section="summary">
          {/* Header */}
          <div className="mb-6 sm:mb-8">
            <h1 className="text-2xl sm:text-3xl lg:text-4xl font-bold mb-2">Maturity Assessment Results</h1>
            <p className="text-muted-foreground text-sm sm:text-base">
              R2R Maturity Assessment for {clientName}
            </p>
          </div>

          {/* Top Summary Cards - Unified Header Layout */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4 w-full mb-6">
            <HeaderCard
              title="Strategic Position"
              value={
                <Tooltip>
                  <TooltipTrigger asChild>
                    <span className="text-teal-400 font-bold">{currentTier}</span>
                  </TooltipTrigger>
                  <TooltipContent className="text-sm text-slate-300 bg-slate-800 border border-slate-700 px-3 py-2 rounded-md shadow-lg max-w-xs">
                    {getTierDescription(currentTier)}
                  </TooltipContent>
                </Tooltip>
              }
              variant="status"
            />
            <HeaderCard
              title="Operational Score"
              value={
                <Tooltip>
                  <TooltipTrigger asChild>
                    <span className="text-white font-semibold">{`${actualOperationalScore} out of 30`}</span>
                  </TooltipTrigger>
                  <TooltipContent className="text-sm text-slate-300 bg-slate-800 border border-slate-700 px-3 py-2 rounded-md shadow-lg max-w-xs">
                    Operational Maturity score — Total score aggregated from 6 operational maturity categories (reconciliation, journals, accruals, etc.)
                  </TooltipContent>
                </Tooltip>
              }
            />
            <HeaderCard
              title="Strategic Score"
              value={
                <Tooltip>
                  <TooltipTrigger asChild>
                    <span className="text-white font-semibold">{`${actualStrategicScore} out of 25`}</span>
                  </TooltipTrigger>
                  <TooltipContent className="text-sm text-slate-300 bg-slate-800 border border-slate-700 px-3 py-2 rounded-md shadow-lg max-w-xs">
                    Strategic Maturity score — Total score aggregated from 5 strategic maturity categories (orchestration, governance, analytics, etc.)
                  </TooltipContent>
                </Tooltip>
              } 
            />
          </div>



          {/* R2R Maturity Positioning Analysis - New Heatmap Display */}
          <div className="w-full mb-6" ref={chartRef}>
            {maturityResults && (
              <MaturitySegmentDisplay 
                opScore={actualOperationalScore} 
                stratScore={actualStrategicScore}
              />
            )}
          </div>

          {/* Strategic Analysis - Three Cards */}
          <section className="bg-slate-800 rounded-xl shadow-md p-4 sm:p-6 mb-8 border border-slate-700">
            <SectionHeader 
              title="Strategic Analysis"
              subtitle="Symptoms, risks and opportunities observed in current process structure"
            />
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              <div className="bg-slate-700 rounded-lg p-3 sm:p-4 flex gap-3 items-start shadow-sm border border-slate-600">
                <Info className="w-5 h-5 sm:w-6 sm:h-6 text-redwoodLightBlue mt-0.5 flex-shrink-0" />
                <div>
                  <div className="text-slate-300 text-sm font-medium mb-1">Symptoms</div>
                  <div className="text-slate-400 text-xs sm:text-sm">
                    {maturitySegmentData?.symptoms || 'Loading symptoms analysis...'}
                  </div>
                </div>
              </div>

              <div className="bg-slate-700 rounded-lg p-3 sm:p-4 flex gap-3 items-start shadow-sm border border-slate-600">
                <AlertTriangle className="w-5 h-5 sm:w-6 sm:h-6 text-tangerine mt-0.5 flex-shrink-0" />
                <div>
                  <div className="text-slate-300 text-sm font-medium mb-1">Risks</div>
                  <div className="text-slate-400 text-xs sm:text-sm">
                    {maturitySegmentData?.risks || 'Loading risks analysis...'}
                  </div>
                </div>
              </div>

              <div className="bg-slate-700 rounded-lg p-3 sm:p-4 flex gap-3 items-start shadow-sm border border-slate-600">
                <Lightbulb className="w-5 h-5 sm:w-6 sm:h-6 text-primary mt-0.5 flex-shrink-0" />
                <div>
                  <div className="text-slate-300 text-sm font-medium mb-1">Opportunities</div>
                  <div className="text-slate-400 text-xs sm:text-sm">
                    {maturitySegmentData?.opportunities || 'Loading opportunities analysis...'}
                  </div>
                </div>
              </div>
            </div>
          </section>

          {/* Strategic Guidance - Recommended Actions */}
          <section className="bg-slate-800 rounded-xl shadow-md p-4 sm:p-6 mb-8 border border-slate-700">
            <SectionHeader 
              title="Strategic Guidance"
              subtitle="Prioritized actions based on your maturity assessment results"
            />
            
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-6">
            {(() => {
              // ========================================
              // 4) ENHANCED UI RENDERING & SEGMENT HIGHLIGHTING AUDIT
              // ========================================

              
              // Apply segment highlighting logic
              let prioritizedActions: RecommendedAction[] = recommendedActionsData ?? [];
              
              if (maturitySegmentData?.highlighted_theme_ids && recommendedActionsData) {
                const highlightedThemeIds = maturitySegmentData.highlighted_theme_ids;
                console.log("🧪 Highlighted themes in segment", { 
                  segmentId: maturitySegmentData.id,
                  highlightedThemeIds 
                });
                
                // Sort actions to prioritize highlighted themes
                prioritizedActions = [...recommendedActionsData].sort((a, b) => {
                  const aIsHighlighted = a.theme_id ? highlightedThemeIds.includes(a.theme_id) : false;
                  const bIsHighlighted = b.theme_id ? highlightedThemeIds.includes(b.theme_id) : false;
                  
                  if (aIsHighlighted && !bIsHighlighted) return -1;
                  if (!aIsHighlighted && bIsHighlighted) return 1;
                  
                  // Secondary sort by priority_score
                  return (b.priority_score || 0) - (a.priority_score || 0);
                });
              }

              
              // Export safety check - Verify data matches between UI and exports with prioritization
              if (maturitySegmentData && prioritizedActions.length > 0) {
                const exportPayload = {
                  symptoms: maturitySegmentData.symptoms,
                  risks: maturitySegmentData.risks,
                  opportunities: maturitySegmentData.opportunities,
                  recommendedActions: prioritizedActions.slice(0, 4).map(
                    (action: RecommendedAction) => ({
                      title: action.action_title,
                      type: action.action_type,
                      outcome: action.expected_outcome,
                      isHighlighted: action.theme_id ? maturitySegmentData.highlighted_theme_ids?.includes(action.theme_id) : false
                    })
                  ),
                  chartImage: !!chartImage,
                  assessmentAnswers: assessmentData?.[0]?.data,
                  segmentHighlighting: !!maturitySegmentData.highlighted_theme_ids
                };
                
                console.log("🧩 Enhanced export safety check", exportPayload);
              }
              
              // Store prioritized actions for use in rendering
              if (prioritizedActions !== recommendedActionsData) {
                // This would be used by the rendering logic below
                console.log("📋 Using prioritized actions in UI");
              }
              
              return null; // This is just for logging, return nothing
            })()}
            
            {(() => {
              // Use prioritized actions if segment highlighting is available
              let actionsToRender: RecommendedAction[] = recommendedActionsData ?? [];
              
              if (maturitySegmentData?.highlighted_theme_ids && recommendedActionsData) {
                const highlightedThemeIds = maturitySegmentData.highlighted_theme_ids;
                
                // Sort actions to prioritize highlighted themes
                actionsToRender = [...recommendedActionsData].sort((a, b) => {
                  const aIsHighlighted = a.theme_id ? highlightedThemeIds.includes(a.theme_id) : false;
                  const bIsHighlighted = b.theme_id ? highlightedThemeIds.includes(b.theme_id) : false;
                  
                  if (aIsHighlighted && !bIsHighlighted) return -1;
                  if (!aIsHighlighted && bIsHighlighted) return 1;
                  
                  // Secondary sort by priority_score
                  return (b.priority_score || 0) - (a.priority_score || 0);
                });
              }
              
              return actionsToRender && actionsToRender.length > 0 ? (
                actionsToRender.slice(0, 4).map((action: RecommendedAction, index: number) => {
                // Map action types to icons and colors
                const getActionIcon = (actionType: string) => {
                  const key = (actionType || '').trim().toLowerCase();
                  switch (key) {
                    case 'foundation': return { Icon: Target, color: 'text-white' };
                    case 'quick win': return { Icon: TrendingUp, color: 'text-white' };
                    case 'governance': return { Icon: Users, color: 'text-white' };
                    case 'analytics': return { Icon: BarChart3, color: 'text-white' };
                    default:
                      console.warn('[Strategic Guidance] Unknown action_type:', actionType);
                      return { Icon: Target, color: 'text-white' };
                  }
                };

                const { Icon, color } = getActionIcon(action.action_type);
                if (!Icon) {
                  console.warn('[Strategic Guidance] No Icon resolved for action type:', action.action_type);
                }

                  // Check if this action is highlighted for this segment
                  const isHighlighted = action.theme_id ? maturitySegmentData?.highlighted_theme_ids?.includes(action.theme_id) : false;

                  return (
                    <div key={action.id} className={`rounded-lg p-3 sm:p-4 border ${
                      isHighlighted 
                        ? 'bg-blue-900/50 border-blue-500/50 ring-1 ring-blue-500/30' 
                        : 'bg-slate-700 border-slate-600'
                    }`}>
                      <div className="flex items-start gap-3">
                        <Icon
                          className={`w-5 h-5 ${color} mt-1 flex-shrink-0`}
                          strokeWidth={1.9}
                          stroke="currentColor"
                          fill="none"
                          aria-hidden="true"
                        />
                        <div>
                          <div className="text-white font-medium text-sm mb-2 flex items-center gap-2">
                            {action.action_title}
                            {isHighlighted && (
                              <span className="text-xs bg-blue-500/20 text-blue-300 px-2 py-1 rounded-full border border-blue-500/30">
                                Priority
                              </span>
                            )}
                          </div>
                          <div className="text-slate-400 text-xs mb-2">{action.action_type} • {action.time_horizon}</div>
                          <div className="text-slate-300 text-xs sm:text-sm">{action.expected_outcome}</div>
                        </div>
                      </div>
                    </div>
                  );
                })
              ) : (
                // Loading state
                <div className="col-span-2 text-center text-slate-400 py-8">
                  Loading strategic guidance recommendations...
                </div>
              );
            })()}
          </div>
        </section>



        </div>

        <div data-pdf-section="details" className="mt-8">
          {/* Implementation Blockers */}
          <section className="bg-slate-800 rounded-xl shadow-md p-4 sm:p-6 mb-8 border border-slate-700">
            <SectionHeader 
              title="Implementation blockers identified"
              subtitle=""
            />
            <div className="flex gap-2 flex-wrap mt-4">
              {latestAssessment?.blockers && latestAssessment.blockers.length > 0 ? (
                latestAssessment.blockers.map((blocker: string, index: number) => (
                  <Badge key={index} variant="outline" className="bg-slate-700 border-slate-600 text-slate-300">
                    {blocker}
                  </Badge>
                ))
              ) : (
                <div className="text-slate-400 text-sm italic">No implementation blockers identified in assessment</div>
              )}
            </div>
          </section>

          {/* Detailed Maturity Responses (extracted component) */}
          <DetailedResults 
            show={showDetailedResponses}
            onShowChange={setShowDetailedResponses}
            assessmentData={assessmentData}
            answers={actualAnswers}
          />

          {/* Maturity Level Reference */}
          <section className="bg-slate-800 rounded-xl shadow-md p-4 sm:p-6 mb-8 border border-slate-700">
            <SectionHeader 
              title="Maturity Level Reference"
              subtitle="Understanding the 5-level maturity scale framework"
            />
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4 sm:gap-6">
              {[1, 2, 3, 4, 5].map((level) => {
                const labels = ['Manual', 'Siloed', 'Managed', 'Controlled', 'Autonomous'];
                const descriptions = [
                  'Manual and offline processes\nLimited visibility, fragmented oversight',
                  'Basic digital tools used independently\nMinimal governance, ad hoc reporting',
                  'Selective automation within process boundaries\nKPIs defined, some exception management in place',
                  'Orchestrated tasks with real-time integration\nProactive monitoring, policy-based automation',
                  'Touchless execution across processes and systems\nContinuous improvement, predictive insights embedded'
                ];
                return (
                  <div key={level} className="text-center">
                    <div className="bg-primary/20 rounded-full w-12 h-12 flex items-center justify-center mx-auto mb-3">
                      <span className="text-primary font-bold">{level}</span>
                    </div>
                    <h4 className="font-semibold text-primary mb-2">{labels[level - 1]}</h4>
                    <p className="text-xs text-muted-foreground whitespace-pre-line">
                      {descriptions[level - 1]}
                    </p>
                  </div>
                );
              })}
            </div>
          </section>

          {/* Recommended Next Step */}
          <div className="bg-slate-800 p-4 sm:p-6 rounded-xl shadow-md border border-slate-700 mb-8">
            <div className="text-white text-lg font-semibold mb-2">Recommended next step</div>
            <p className="text-slate-300 text-sm mb-4">
              Review your maturity results with internal stakeholders to align on automation gaps,
              validate priorities, and assess readiness for a deeper evaluation. If helpful, we can
              support this process with structured next steps.
            </p>
          </div>

          {/* Export Actions */}
          <div className="flex gap-4 justify-center flex-wrap" data-pdf-hidden="true">
            <div className="flex gap-2">
              <button
                onClick={handleGeneratePdf}
                className="flex items-center gap-2 bg-primary hover:bg-primaryHover text-forestBlack font-medium px-3 py-2 rounded-md transition-colors disabled:opacity-70 disabled:cursor-not-allowed"
                title="Download PDF"
                data-cy="generate-pdf"
                disabled={isGeneratingPdf}
                aria-busy={isGeneratingPdf}
              >
                {isGeneratingPdf ? (
                  <>
                    <span
                      className="h-4 w-4 border-2 border-forestBlack/40 border-t-forestBlack rounded-full animate-spin"
                      aria-hidden="true"
                    />
                    Generating…
                  </>
                ) : (
                  <>
                    <FileText className="h-4 w-4" />
                    Generate PDF
                  </>
                )}
              </button>
            </div>

          </div>
        </div>
        </div>

        {maturityResults && latestAssessment && (
          <div
            ref={reportDocRef}
            aria-hidden="true"
            style={{
              position: 'fixed',
              top: 0,
              left: '-10000px',
              width: '794px',
              pointerEvents: 'none',
              opacity: 0,
              zIndex: -1,
            }}
          >
            <MaturityReportDocument
              maturityResults={maturityResults}
              clientName={clientName}
              projectName={(projectData as any)?.name}
              exportDate={exportDate}
              operationalAnswers={operationalAnswers}
              strategicAnswers={strategicAnswers}
              maturitySegmentData={maturitySegmentData ?? null}
              recommendedActionsData={recommendedActionsData ?? null}
              includeExecutiveSummary={false}
              includeMaturityMatrix={false}
              includeStrategicAnalysis={false}
              includeStrategicGuidance={false}
              includeAppendix={false}
            />
          </div>
        )}
      </TooltipProvider>
    </MaturityLayout>
  );
}

export default MaturityOnlyResults;
